import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Paper,
  Divider,
  Link,
  IconButton
} from '@mui/material';
import {
  Search,
  Support,
  Email,
  Facebook,
  Twitter,
  LinkedIn,
  Clear
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import FAQAccordion from '../components/FAQAccordion';
import TutorialCard from '../components/TutorialCard';
import SupportForm from '../components/SupportForm';
import { setSearchQuery, clearSearch } from '../redux/slices/helpSlice';
import './HelpCenter.css';

const HelpCenter = () => {
  const dispatch = useDispatch();
  const { faqs, tutorials, searchQuery, filteredFaqs } = useSelector(state => state.help);
  
  const [searchInput, setSearchInput] = useState('');
  const [supportFormOpen, setSupportFormOpen] = useState(false);

  // Update search when input changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(setSearchQuery(searchInput));
    }, 300); // Debounce search
    
    return () => clearTimeout(timeoutId);
  }, [searchInput, dispatch]);

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    dispatch(clearSearch());
  };

  const handleTutorialClick = (tutorial) => {
    // Mock navigation to tutorial detail page
    console.log('Navigate to tutorial:', tutorial.slug);
    // In a real app: navigate(`/aide/${tutorial.slug}`);
  };

  const displayedFaqs = searchQuery ? filteredFaqs : faqs;

  return (
    <>
      <Helmet>
        <title>Centre d'aide | Mission Sport</title>
        <meta 
          name="description" 
          content="Trouvez des réponses rapides à vos questions sur Mission Sport. FAQ, tutoriels et support pour les organisations sportives et professionnels indépendants." 
        />
        <meta property="og:title" content="Centre d'aide | Mission Sport" />
        <meta 
          property="og:description" 
          content="Trouvez des réponses rapides à vos questions sur Mission Sport. FAQ, tutoriels et support pour les organisations sportives et professionnels indépendants." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://missionsport.com/aide" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Centre d'aide | Mission Sport" />
        <meta 
          name="twitter:description" 
          content="Trouvez des réponses rapides à vos questions sur Mission Sport. FAQ, tutoriels et support." 
        />
      </Helmet>

      <div className="help-center">
        {/* Header Section */}
        <Box className="help-header" sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                Centre d'aide Mission Sport
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4, 
                  opacity: 0.9,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  lineHeight: 1.4
                }}
              >
                Trouvez des réponses rapides à vos questions ou contactez notre support
              </Typography>
              
              {/* Search Bar */}
              <Paper 
                elevation={3}
                sx={{ 
                  p: 1, 
                  display: 'flex', 
                  alignItems: 'center',
                  maxWidth: 600,
                  mx: 'auto',
                  borderRadius: 3
                }}
              >
                <Search sx={{ color: 'text.secondary', mr: 1, ml: 1 }} />
                <TextField
                  placeholder="Rechercher dans les FAQ..."
                  variant="standard"
                  fullWidth
                  value={searchInput}
                  onChange={handleSearchChange}
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: '1.1rem' }
                  }}
                  aria-label="Rechercher dans les questions fréquentes"
                />
                {searchInput && (
                  <IconButton 
                    onClick={handleClearSearch}
                    size="small"
                    aria-label="Effacer la recherche"
                  >
                    <Clear />
                  </IconButton>
                )}
              </Paper>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 6 }}>
          {/* FAQ Section */}
          <Box sx={{ mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 1,
                fontWeight: 600,
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}
            >
              Questions fréquentes
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                textAlign: 'center', 
                mb: 4, 
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              {searchQuery 
                ? `Résultats pour "${searchQuery}" (${filteredFaqs.length} résultat${filteredFaqs.length > 1 ? 's' : ''})`
                : 'Trouvez rapidement les réponses à vos questions les plus courantes'
              }
            </Typography>
            
            <FAQAccordion faqs={displayedFaqs} searchQuery={searchQuery} />
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Tutorials Section */}
          <Box sx={{ mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 1,
                fontWeight: 600,
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}
            >
              Guides rapides
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                textAlign: 'center', 
                mb: 4, 
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Suivez nos tutoriels étape par étape pour maîtriser Mission Sport
            </Typography>
            
            <Grid container spacing={3}>
              {tutorials.map((tutorial) => (
                <Grid item xs={12} sm={6} md={3} key={tutorial.id}>
                  <TutorialCard 
                    tutorial={tutorial} 
                    onClick={handleTutorialClick}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ my: 6 }} />

          {/* Support Contact Section */}
          <Box sx={{ mb: 8 }}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
              }}
            >
              <Support sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography 
                variant="h4" 
                component="h2" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                Besoin d'aide personnalisée ?
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3, 
                  color: 'text.secondary',
                  maxWidth: 500,
                  mx: 'auto',
                  lineHeight: 1.6
                }}
              >
                Notre équipe support est là pour vous accompagner. 
                Contactez-nous et nous vous répondrons dans les plus brefs délais.
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                startIcon={<Email />}
                onClick={() => setSupportFormOpen(true)}
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}
              >
                Contacter le support
              </Button>
            </Paper>
          </Box>

          {/* Useful Links Section */}
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                textAlign: 'center', 
                mb: 4,
                fontWeight: 600,
                fontSize: { xs: '1.5rem', md: '2rem' }
              }}
            >
              Liens utiles
            </Typography>
            
            <Grid container spacing={2} justifyContent="center">
              {[
                { label: 'Conditions générales d\'utilisation', href: '/terms' },
                { label: 'Politique de confidentialité', href: '/privacy' },
                { label: 'Politique de remboursement', href: '/refund-policy' },
                { label: 'Guide d\'utilisation', href: '/user-guide' }
              ].map((link, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Link 
                    href={link.href}
                    sx={{ 
                      display: 'block',
                      p: 2,
                      textAlign: 'center',
                      textDecoration: 'none',
                      color: 'primary.main',
                      fontWeight: 500,
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'white',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {link.label}
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>

        {/* Dedicated Footer */}
        <Box 
          component="footer" 
          className="help-footer"
          sx={{ 
            bgcolor: 'grey.100', 
            py: 4,
            borderTop: '1px solid',
            borderColor: 'grey.300'
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Support Mission Sport
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Email : support@missionsport.com
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Nous répondons généralement sous 24h
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                  <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                    Suivez-nous
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'center', md: 'flex-end' } }}>
                    <IconButton 
                      href="https://facebook.com/missionsport" 
                      target="_blank"
                      aria-label="Facebook"
                      sx={{ color: 'primary.main' }}
                    >
                      <Facebook />
                    </IconButton>
                    <IconButton 
                      href="https://twitter.com/missionsport" 
                      target="_blank"
                      aria-label="Twitter"
                      sx={{ color: 'primary.main' }}
                    >
                      <Twitter />
                    </IconButton>
                    <IconButton 
                      href="https://linkedin.com/company/missionsport" 
                      target="_blank"
                      aria-label="LinkedIn"
                      sx={{ color: 'primary.main' }}
                    >
                      <LinkedIn />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Support Form Dialog */}
        <SupportForm 
          open={supportFormOpen} 
          onClose={() => setSupportFormOpen(false)} 
        />
      </div>
    </>
  );
};

export default HelpCenter;